<template>
  <div class="ci-summary-group">
    <div class="ci-summary-title ci-card-title ci-accent ci-accent--success">
      <div class="ci-summary-head">
        <div class="ci-summary-head-title">{{ headerTitle }}</div>

        <div class="ci-summary-head-actions">
          <label class="ci-toggle" title="Toggle cleared-only view">
            <input type="checkbox" v-model="isClearedOnly" />
            <span class="ci-toggle__track">
              <span class="ci-toggle__thumb"></span>
            </span>
            <span class="ci-toggle__label">Cleared only</span>
          </label>

          <select class="ci-trend-select" v-model="selectedTrend">
            <option v-for="t in trends" :key="t.value" :value="t.value">
              {{ t.label }}
            </option>
          </select>
        </div>
      </div>

      <span class="ci-summary-subtitle">
        <span class="ci-kpi-row">
          <span class="ci-kpi-value">{{ valueText }}</span>
        </span>
      </span>
    </div>

    <div class="ci-summary-cards ci-scroll">
      <div v-if="loading" class="ci-summary-loading">Loading...</div>
      <div v-else-if="error" class="ci-empty-inline ci-empty-inline--error">{{ error }}</div>

      <template v-else>
        <div v-if="rows.length" class="ci-list">
          <button
            v-for="r in rows"
            :key="r.title"
            class="ci-summary-row ci-summary-row--click"
            type="button"
            @click="openModal(r)"
          >
            <div class="ci-summary-name" :title="r.title">
              {{ r.title }}
            </div>

            <div class="ci-summary-right">
              <div class="ci-metric-pill ci-metric-pill--muted">
                <span class="ci-pill-label">{{ metricLabel }}</span>
                <span class="ci-pill-value">{{ r.count }}</span>
              </div>
            </div>
          </button>
        </div>

        <div v-else class="ci-empty-inline">No data</div>
      </template>
    </div>

    <div v-if="showModal" class="ci-modal" @click.self="closeModal">
      <div class="ci-modal__panel">
        <div class="ci-modal__header">
          <div class="ci-modal__head-left">
            <div class="ci-modal__title ci-card-title" :title="selectedTitle">{{ selectedTitle }}</div>

            <div class="ci-modal__meta">
              <div class="ci-metric-pill ci-metric-pill--muted">
                <span class="ci-pill-label">{{ modalMetricLabel }}</span>
                <span class="ci-pill-value">{{ modalCountText }}</span>
              </div>
            </div>
          </div>

          <div class="ci-modal__head-right">
            <div class="ci-modal-filter">
              <button
                class="ci-chip ci-chip--sm"
                :class="{ 'ci-chip--active': modalFilter === 'notCleared' }"
                type="button"
                @click="modalFilter = 'notCleared'"
              >
                Not Clear
              </button>
              <button
                class="ci-chip ci-chip--sm"
                :class="{ 'ci-chip--active': modalFilter === 'cleared' }"
                type="button"
                @click="modalFilter = 'cleared'"
              >
                Cleared
              </button>
            </div>

            <button class="ci-modal__close" type="button" @click="closeModal">✕</button>
          </div>
        </div>

        <div class="ci-modal__body ci-scroll">
          <div v-if="detailsLoading" class="ci-summary-loading">Loading details...</div>
          <div v-else-if="detailsError" class="ci-empty-inline ci-empty-inline--error">{{ detailsError }}</div>

          <template v-else>
            <div v-if="filteredDetailRows.length" class="ci-detail-list">
              <div v-for="d in filteredDetailRows" :key="detailKey(d)" class="ci-detail-card">
                <div class="ci-detail-title-row">
                  <div class="ci-detail-title" :title="d.title">{{ d.title }}</div>

                  <button
                    class="ci-open-swift"
                    type="button"
                    @click.stop="openLoanTasks(d.resourceId, d.conditionId)"
                    :disabled="!(d.resourceId) || !(d.conditionId)"
                    :title="!(d.resourceId) || !(d.conditionId) ? 'Missing loan id / condition id' : 'Open in Swift'"
                  >
                    Open in Swift
                  </button>
                </div>

                <div class="ci-detail-subrow">
                  <span class="ci-sub-pill">
                    <span class="ci-sub-pill__k">Decision</span>
                    <span class="ci-sub-pill__v">{{ decisionLabel(d.decision) }}</span>
                  </span>
                </div>

                <div class="ci-detail-chips"></div>

                <div v-if="d.summary" class="ci-detail-block">
                  <div class="ci-detail-label">Summary</div>
                  <div class="ci-detail-text">{{ d.summary }}</div>
                </div>

                <div v-if="d.notes" class="ci-detail-block ci-detail-block--muted">
                  <div class="ci-detail-label">Notes</div>
                  <div class="ci-detail-text">{{ d.notes }}</div>
                </div>

                <div v-if="d.loanSubjectPropertyAddress || d.documentAddress" class="ci-detail-addr">
                  <div v-if="d.loanSubjectPropertyAddress" class="ci-addr-row">
                    <div class="ci-addr-k">Loan</div>
                    <div class="ci-addr-v">{{ d.loanSubjectPropertyAddress }}</div>
                  </div>

                  <div v-if="d.documentAddress" class="ci-addr-row">
                    <div class="ci-addr-k">Doc</div>
                    <div class="ci-addr-v">{{ d.documentAddress }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="ci-empty-inline">No detail rows</div>
          </template>
        </div>

        <div class="ci-modal__footer">
          <button class="ci-btn" type="button" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"

const CLEARED_ONLY_KEY = "swiftAiProcessedSummaryListCard.clearedOnly"

export default {
  name: "SwiftAiProcessedSummaryListCard",
  props: {
    authToken: { type: String, default: "" }
  },
  data() {
    return {
      loading: false,
      error: "",
      valueText: "--",
      titlesText: "--",
      rows: [],

      trends: [
        { value: "all", label: "All time" },
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "thisWeek", label: "This week" },
        { value: "lastWeek", label: "Last week" },
        { value: "thisMonth", label: "This month" },
        { value: "lastMonth", label: "Last month" },
        { value: "rolling90", label: "Rolling 90" }
      ],
      selectedTrend: "all",
      startDate: null,
      endDate: null,

      isClearedOnly: false,

      showModal: false,
      selectedRow: null,

      modalFilter: "notCleared",
      modalCount: null,

      detailsLoading: false,
      detailsError: "",
      detailRows: []
    }
  },
  mounted() {
    const saved = window.localStorage.getItem(CLEARED_ONLY_KEY)
    if (saved === "true") this.isClearedOnly = true
    if (saved === "false") this.isClearedOnly = false
  },
  computed: {
    headerTitle() {
      return this.isClearedOnly ? "Cleared by swiftAI" : "Processed by swiftAI"
    },
    metricLabel() {
      return this.isClearedOnly ? "Cleared" : "Not Clear"
    },
    modalMetricLabel() {
      return this.modalFilter === "cleared" ? "Cleared" : "Not Clear"
    },
    selectedTitle() {
      return this.selectedRow?.title || ""
    },
    modalCountText() {
      const v = this.modalCount
      if (v === null || v === undefined) return ""
      return String(v)
    },
    filteredDetailRows() {
      const list = Array.isArray(this.detailRows) ? this.detailRows : []

      const filteredByDecision = list.filter(x => {
        const d = this.normalizeDecision(x?.decision)
        if (this.modalFilter === "cleared") return d === "clear"
        return d === "not_clear"
      })

      if (!this.startDate && !this.endDate) return filteredByDecision

      const startMs = this.startDate ? new Date(this.startDate).getTime() : null
      const endMs = this.endDate ? new Date(this.endDate).getTime() : null

      return filteredByDecision.filter(x => {
        const raw = x?.createdAt || x?.CreatedAt
        if (!raw) return true
        const t = new Date(raw).getTime()
        if (Number.isNaN(t)) return true
        if (startMs !== null && t < startMs) return false
        if (endMs !== null && t > endMs) return false
        return true
      })
    }
  },
  methods: {
    extractRoot(payload) {
      return payload?.data?.data || payload?.data || payload || {}
    },
    applyTrend() {
      const now = new Date()
      const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const endOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59)

      const startOfWeek = d => {
        const x = startOfDay(d)
        const day = x.getDay()
        const diff = (day + 6) % 7
        x.setDate(x.getDate() - diff)
        return x
      }

      const startOfMonth = d => new Date(d.getFullYear(), d.getMonth(), 1)
      const endOfMonth = d => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59)

      if (this.selectedTrend === "all") {
        this.startDate = null
        this.endDate = null
        return
      }

      if (this.selectedTrend === "today") {
        this.startDate = startOfDay(now)
        this.endDate = endOfDay(now)
        return
      }

      if (this.selectedTrend === "yesterday") {
        const y = new Date(now)
        y.setDate(y.getDate() - 1)
        this.startDate = startOfDay(y)
        this.endDate = endOfDay(y)
        return
      }

      if (this.selectedTrend === "thisWeek") {
        this.startDate = startOfWeek(now)
        this.endDate = endOfDay(now)
        return
      }

      if (this.selectedTrend === "lastWeek") {
        const s = startOfWeek(now)
        s.setDate(s.getDate() - 7)
        const e = new Date(s)
        e.setDate(e.getDate() + 6)
        this.startDate = startOfDay(s)
        this.endDate = endOfDay(e)
        return
      }

      if (this.selectedTrend === "thisMonth") {
        this.startDate = startOfMonth(now)
        this.endDate = endOfDay(now)
        return
      }

      if (this.selectedTrend === "lastMonth") {
        const s = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const e = endOfMonth(s)
        this.startDate = startOfDay(s)
        this.endDate = e
        return
      }

      if (this.selectedTrend === "rolling90") {
        const s = startOfDay(now)
        s.setDate(s.getDate() - 89)
        this.startDate = s
        this.endDate = endOfDay(now)
      }
    },
    toIsoParam(v) {
      if (!v) return null
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return null
      return d.toISOString()
    },
    normalizeSummaryRows(list) {
      const src = Array.isArray(list) ? list : []
      return src
        .map(x => ({
          title: (x?.Title ?? x?.title ?? "").trim(),
          count: Number(x?.ClearedCount ?? x?.clearedCount ?? x?.count ?? 0)
        }))
        .filter(x => x.title)
    },
    normalizeDecision(v) {
      const s = String(v || "").trim().toLowerCase()
      if (s === "not_clear" || s === "notclear" || s === "not clear") return "not_clear"
      if (s === "clear" || s === "cleared") return "clear"
      return s
    },
    decisionLabel(v) {
      const d = this.normalizeDecision(v)
      if (d === "not_clear") return "Not Clear"
      if (d === "clear") return "Clear"
      return String(v || "")
    },
    async fetchData() {
      if (!this.authToken) return

      this.loading = true
      this.error = ""
      try {
        const start = this.toIsoParam(this.startDate)
        const end = this.toIsoParam(this.endDate)

        const resp = this.isClearedOnly
          ? await ApiService.GetSwiftClearedConditionsSummary(this.authToken, start, end)
          : await ApiService.GetSwiftNotClearedConditionsSummary(this.authToken, start, end)

        const root = this.extractRoot(resp)
        const list = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : []
        this.rows = this.normalizeSummaryRows(list).sort((a, b) => b.count - a.count)

        let total = 0
        for (const r of this.rows) total += Number(r.count || 0)

        this.valueText = String(total)
        this.titlesText = String(this.rows.length)
      } catch (e) {
        this.error = e?.message || "Failed to load"
      } finally {
        this.loading = false
      }
    },
    async fetchModalCount() {
      if (!this.authToken || !this.selectedRow?.title) return

      const start = this.toIsoParam(this.startDate)
      const end = this.toIsoParam(this.endDate)

      const resp =
        this.modalFilter === "cleared"
          ? await ApiService.GetSwiftClearedConditionsSummary(this.authToken, start, end)
          : await ApiService.GetSwiftNotClearedConditionsSummary(this.authToken, start, end)

      const root = this.extractRoot(resp)
      const list = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : []
      const rows = this.normalizeSummaryRows(list)
      const hit = rows.find(x => x.title === this.selectedRow.title)
      this.modalCount = hit ? hit.count : 0
    },
    detailKey(d) {
      return String(d.decisionId || "") + "-" + String(d.conditionId || "") + "-" + String(d.createdAt || d.CreatedAt || "")
    },
    async openModal(r) {
      this.selectedRow = r
      this.showModal = true
      this.detailsError = ""
      this.detailRows = []
      this.modalFilter = this.isClearedOnly ? "cleared" : "notCleared"
      this.modalCount = r?.count ?? 0
      await this.fetchDetails()
    },
    closeModal() {
      this.showModal = false
      this.selectedRow = null
      this.modalFilter = "notCleared"
      this.modalCount = null
      this.detailsLoading = false
      this.detailsError = ""
      this.detailRows = []
    },
    async fetchDetails() {
      if (!this.selectedRow?.title) {
        this.detailsError = "Missing title"
        return
      }

      this.detailsLoading = true
      this.detailsError = ""

      try {
        const resp =
          this.modalFilter === "cleared"
            ? await ApiService.GetSwiftClearedConditionsDrillIn(this.selectedRow.title, this.authToken)
            : await ApiService.GetSwiftNotClearedConditionsDrillIn(this.selectedRow.title, this.authToken)

        const root = this.extractRoot(resp)
        const list = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : []
        this.detailRows = Array.isArray(list) ? list : []
      } catch (e) {
        this.detailsError = e?.message || "Failed to load details"
      } finally {
        this.detailsLoading = false
      }
    },
    openLoanTasks(loanId, condId) {
      if (!loanId || !condId) return
      const isDev = window.location.hostname === "localhost" || window.location.hostname === "dev.swiftapp.ai"
      const baseUrl = isDev ? "https://dev-swift.goldstarfinancial.com" : "https://swift.goldstarfinancial.com"
      window.open(`${baseUrl}?id=${loanId}&condid=${condId}`, "_blank")
    }
  },
  watch: {
    authToken: {
      immediate: true,
      handler() {
        this.applyTrend()
        this.fetchData()
      }
    },
    selectedTrend() {
      this.applyTrend()
      this.fetchData()
    },
    isClearedOnly(v) {
      window.localStorage.setItem(CLEARED_ONLY_KEY, v ? "true" : "false")
      this.fetchData()
    },
    modalFilter() {
      if (!this.showModal) return
      this.fetchModalCount()
      this.fetchDetails()
    }
  }
}
</script>

<style scoped>
.ci-summary-head-actions{
  display:flex;
  align-items:center;
  gap:10px;
  flex:0 0 auto
}

.ci-modal__head-right{
  display:flex;
  align-items:center;
  gap:10px
}

.ci-toggle{
  display:inline-flex;
  align-items:center;
  gap:8px;
  cursor:pointer;
  user-select:none
}

.ci-toggle input{
  position:absolute;
  opacity:0;
  pointer-events:none
}

.ci-toggle__track{
  width:38px;
  height:22px;
  border-radius:999px;
  border:1px solid rgba(15, 23, 42, 0.18);
  background: rgba(15, 23, 42, 0.06);
  display:inline-flex;
  align-items:center;
  padding:2px;
  box-sizing:border-box
}

.ci-toggle__thumb{
  width:18px;
  height:18px;
  border-radius:999px;
  background: var(--text-primary-on-background);
  transform: translateX(0);
  transition: transform .14s ease
}

.ci-toggle input:checked + .ci-toggle__track .ci-toggle__thumb{
  transform: translateX(16px)
}

.ci-toggle__label{
  font-weight:950;
  font-size:12px;
  white-space:nowrap;
  color: var(--text-primary-on-background)
}

.ci-trend-select:focus{
  outline:none;
  box-shadow: 0 0 0 2px rgba(39, 174, 96, 0.18);
}

.ci-accent--success{ --accent-color:#27AE60; }

.ci-accent{
  border-left:10px solid var(--accent-color);
  border-top-left-radius:12px;
  background-color: #27AE60;
}

.ci-summary-title{
  border-bottom: 1px solid var(--accent-color);
  background: rgba(39, 174, 96, 0.10);
  color: #27AE60;
}

.ci-chip{
  height:28px;
  border-radius:999px;
  padding:0 10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor:pointer;
  font-weight: 950;
  font-size: 12px;
  color: var(--text-primary-on-background)
}

.ci-chip:hover{
  background: rgba(15, 23, 42, 0.06)
}

.ci-open-swift{
  flex: 0 0 auto;
  height: 28px;
  border-radius: 10px;
  padding: 0 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer;
  font-weight: 950;
  font-size: 12px;
  white-space: nowrap
}

.ci-open-swift:hover{
  background: rgba(15, 23, 42, 0.06)
}

.ci-open-swift:disabled{
  opacity: .55;
  cursor: not-allowed
}

.ci-modal-filter{
  display:flex;
  gap:8px;
  flex-wrap:wrap
}

.ci-chip--sm{
  height:26px;
  padding:0 10px;
  font-size:12px
}

.ci-chip--active{
  border-color: rgba(39, 174, 96, 0.55);
  box-shadow: 0 0 0 2px rgba(39, 174, 96, 0.14);
  background: rgba(39, 174, 96, 0.10)
}



.ci-summary-head-actions{
  display:flex;
  align-items:center;
  gap:10px;
  flex:0 0 auto
}

.ci-modal__head-right{
  display:flex;
  align-items:center;
  gap:10px
}

.ci-toggle{
  display:inline-flex;
  align-items:center;
  gap:8px;
  cursor:pointer;
  user-select:none
}

.ci-toggle input{
  position:absolute;
  opacity:0;
  pointer-events:none
}

.ci-toggle__track{
  width:38px;
  height:22px;
  border-radius:999px;
  border:1px solid rgba(15, 23, 42, 0.18);
  background: rgba(15, 23, 42, 0.06);
  display:inline-flex;
  align-items:center;
  padding:2px;
  box-sizing:border-box
}

.ci-toggle__thumb{
  width:18px;
  height:18px;
  border-radius:999px;
  background: var(--text-primary-on-background);
  transform: translateX(0);
  transition: transform .14s ease
}

.ci-toggle input:checked + .ci-toggle__track .ci-toggle__thumb{
  transform: translateX(16px)
}

.ci-toggle__label{
  font-weight:950;
  font-size:12px;
  white-space:nowrap;
  color: var(--text-primary-on-background)
}

.ci-toggle--sm .ci-toggle__track{
  width:34px;
  height:20px
}

.ci-toggle--sm .ci-toggle__thumb{
  width:16px;
  height:16px
}

.ci-toggle--sm input:checked + .ci-toggle__track .ci-toggle__thumb{
  transform: translateX(14px)
}

.ci-trend-select:focus{
  outline:none;
  box-shadow: 0 0 0 2px rgba(39, 174, 96, 0.18);
}

.ci-accent--success{ --accent-color:#27AE60; }

.ci-accent{
  border-left:10px solid var(--accent-color);
  border-top-left-radius:12px;
  background-color: #27AE60;
}

.ci-summary-title{
  border-bottom: 1px solid var(--accent-color);
  background: rgba(39, 174, 96, 0.10);
  color: #27AE60;
}

.ci-filter-row{
  display:flex;
  gap:8px;
  margin-bottom:10px;
  flex-wrap:wrap
}

.ci-chip{
  height:28px;
  border-radius:999px;
  padding:0 10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor:pointer;
  font-weight: 950;
  font-size: 12px;
  color: var(--text-primary-on-background)
}

.ci-chip:hover{
  background: rgba(15, 23, 42, 0.06)
}

.ci-chip--active{
  border-color: rgba(242, 153, 74, 0.55);
  box-shadow: 0 0 0 2px rgba(242, 153, 74, 0.14);
  background: rgba(242, 153, 74, 0.10)
}

.ci-filter-row{
  display:flex;
  gap:8px;
  margin-bottom:10px;
  flex-wrap:wrap
}

.ci-chip{
  height:28px;
  border-radius:999px;
  padding:0 10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor:pointer;
  font-weight: 950;
  font-size: 12px;
  color: var(--text-primary-on-background)
}

.ci-chip:hover{
  background: rgba(15, 23, 42, 0.06)
}

.ci-chip--active{
  border-color: rgba(242, 153, 74, 0.55);
  box-shadow: 0 0 0 2px rgba(242, 153, 74, 0.14);
  background: rgba(242, 153, 74, 0.10)
}

.ci-open-swift{
  flex: 0 0 auto;
  height: 28px;
  border-radius: 10px;
  padding: 0 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer;
  font-weight: 950;
  font-size: 12px;
  white-space: nowrap
}

.ci-open-swift:hover{
  background: rgba(15, 23, 42, 0.06)
}

.ci-open-swift:disabled{
  opacity: .55;
  cursor: not-allowed
}

.ci-summary-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  width:100%;
  height:28px
}

.ci-summary-head-title{
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.ci-trend-select{
  height:28px;
  border-radius:10px;
  padding:0 10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  font-weight: 950;
  font-size: 12px;
  color: var(--text-primary-on-background);
  cursor:pointer;
}

.ci-trend-select:focus{
  outline:none;
  box-shadow: 0 0 0 2px rgba(242, 153, 74, 0.18);
}

.ci-accent--productivity{ --accent-color:#2F80ED; }
.ci-accent--success{ --accent-color:#27AE60; }
.ci-accent--attention{ --accent-color:#F2994A; }
.ci-accent--insight{ --accent-color:#9B51E0; }

.ci-accent{
  border-left:10px solid var(--accent-color);
  border-top-left-radius:12px;
  background-color: #27AE60;
}

.ci-summary-group {
  min-height: 0;
  height: 100%;
  border: 1px solid var(--border-ltr);
  border-radius: 5px;
  background: var(--background);
  overflow: hidden;
  display: flex;
  flex-direction: column
}

.ci-summary-title {
  padding: 10px 12px;
  border-bottom: 1px solid var(--accent-color);
  background: rgba(39, 174, 96, 0.10);

  font-weight: 950;
  font-size: 12px;
  color: #27AE60;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height:50px;
}

.ci-summary-subtitle {
  font-size: 11px;
  font-weight: 900;
  align-items: center;
  display: flex;
  gap:10px;
  color: var(--text-secondary-on-background)
}

.ci-summary-cards {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px
}

.ci-scroll {
  overflow: auto
}

.ci-summary-loading {
  height: 74px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 950;
  color: var(--text-secondary-on-background)
}

.ci-empty-inline {
  padding: 18px 10px;
  text-align: center;
  font-weight: 950;
  color: var(--text-secondary-on-background)
}

.ci-empty-inline--error {
  color: rgba(180, 0, 0, 0.80)
}

.ci-kpi-value {
  font-size: 28px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  line-height: 1
}

.ci-kpi-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap
}

.ci-kpi-hint {
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background)
}

.ci-divider {
  height: 1px;
  background: var(--border-ltr);
  opacity: .9;
  margin: 2px 0 6px 0
}

.ci-list {
  display: flex;
  flex-direction: column;
  gap: 10px
}

.ci-summary-row {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 5px;
  padding: 10px 12px;
  background: rgba(15, 23, 42, 0.015);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 44px;
  text-align: left;
  font: inherit
}

.ci-summary-row--click {
  cursor: pointer;
  outline: none
}

.ci-summary-row--click:hover {
  background: rgba(15, 23, 42, 0.03)
}

.ci-summary-name {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0
}

.ci-summary-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px
}

.ci-metric-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(40, 110, 255, 0.08);
  border: 1px solid rgba(40, 110, 255, 0.15)
}

.ci-metric-pill--muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.10)
}

.ci-pill-label {
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background)
}

.ci-pill-value {
  font-size: 14px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  line-height: 1
}

.ci-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  z-index: 9999
}

.ci-modal__panel {
  width: min(980px, 100%);
  max-height: min(85vh, 900px);
  background: var(--background);
  border-radius: 14px;
  border: 1px solid var(--border-ltr);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column
}

.ci-modal__header {
  padding: 12px;
  border-bottom: 1px solid var(--border-ltr);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: rgba(5, 29, 58, 0.03)
}

.ci-modal__head-left {
  min-width: 0
}

.ci-modal__title {
  font-weight: 950;
  font-size: 13px;
  color: var(--text-primary-on-background);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis
}

.ci-modal__meta {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap
}

.ci-modal__close {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer
}

.ci-modal__close:hover {
  background: rgba(15, 23, 42, 0.06)
}

.ci-modal__body {
  padding: 12px;
  overflow: auto;
  min-height: 0
}

.ci-modal__footer {
  padding: 12px;
  border-top: 1px solid var(--border-ltr);
  display: flex;
  justify-content: flex-end
}

.ci-btn {
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer;
  font-weight: 950;
  font-size: 12px
}

.ci-btn:hover {
  background: rgba(15, 23, 42, 0.06)
}

.ci-detail-list {
  display: flex;
  flex-direction: column;
  gap: 10px
}

.ci-detail-card {
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 5px;
  background: rgba(15, 23, 42, 0.01);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px
}

.ci-detail-label {
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background)
}

.ci-detail-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap
}

.ci-detail-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px
}

.ci-detail-title {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0
}

.ci-detail-subrow {
  display: flex;
  gap: 8px;
  flex-wrap: wrap
}

.ci-sub-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.03);
  border: 1px solid rgba(15, 23, 42, 0.10)
}

.ci-sub-pill__k {
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background)
}

.ci-sub-pill__v {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  line-height: 1
}

.ci-detail-block {
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(15, 23, 42, 0.01)
}

.ci-detail-block--muted {
  background: rgba(15, 23, 42, 0.008)
}

.ci-detail-text {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.75);
  white-space: pre-wrap
}

.ci-detail-addr {
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(40, 110, 255, 0.04)
}

.ci-addr-row {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 10px;
  align-items: start
}

.ci-addr-row+.ci-addr-row {
  margin-top: 8px
}

.ci-addr-k {
  font-size: 11px;
  font-weight: 950;
  color: var(--text-secondary-on-background)
}

.ci-addr-v {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  word-break: break-word
}
.ci-modal-filter{
  display:flex;
  gap:8px;
  flex-wrap:wrap
}

.ci-chip--sm{
  height:26px;
  padding:0 10px;
  font-size:12px
}

.ci-chip--active{
  border-color: rgba(39, 174, 96, 0.55);
  box-shadow: 0 0 0 2px rgba(39, 174, 96, 0.14);
  background: rgba(39, 174, 96, 0.10)
}
</style>
